import flatten from 'lodash/flatten';
import uniq from 'lodash/uniq';

import { ContractCreationParamsData, ContractUpdateParamsData } from 'chaika-frontend-types/frontend-cms';
import { ContractStatus } from 'chaika-enums';

import { store } from '@store';
import { ContractApi } from '@organization/api/ContractApi';
import { getUniqNewIds } from '@utils/getUniqNewIds';

import { legalEntityBranchService } from '../legalEntityBranch/legalEntityBranchService';
import { loadWithChunkAndFlatten } from '@utils/loadWithChunkAndFlatten';

class ContractService {
    private isAllLoaded = false;
    public async load(): Promise<void> {
        if (!this.isAllLoaded) {
            this.isAllLoaded = true;
            const contractList = store.contractList;

            const contracts = await ContractApi.getList({
                status: [ContractStatus.Active, ContractStatus.Archive].join(','),
            });
            const legalEntityIds = uniq(flatten(contracts.map(({ legalEntityIds }) => legalEntityIds)));

            contractList.addLoadedLegalEntitiesIds(legalEntityIds);
            contractList.add(contracts);
        }
    }

    public async loadByIds(ids: string[]): Promise<void> {
        const contractList = store.contractList;

        const newIds = getUniqNewIds(contractList.ids, ids);

        if (newIds.length > 0) {
            const contracts = await loadWithChunkAndFlatten(newIds, ContractApi.getList, 'id');

            contractList.add(contracts);
        }
    }

    public async loadByOrganizationIds(organizationIds: string[]): Promise<void> {
        const { legalEntityBranchList } = store;

        await legalEntityBranchService.loadByOrganizationIds(organizationIds);

        const legalEntityIds = legalEntityBranchList.getLegalEntityIdsForOrganizations(organizationIds);
        await this.loadLegalEntityContracts(legalEntityIds);
    }

    public async loadLegalEntityContracts(legalEntityIds: string[]): Promise<void> {
        const contractList = store.contractList;
        const notLoadedIds = contractList.getNotLoadedLegalEntitiesIds(legalEntityIds);
        if (notLoadedIds.length > 0) {
            const contracts = await ContractApi.getList({
                relatedLegalEntityId: legalEntityIds.join(','),
                status: [ContractStatus.Active, ContractStatus.Archive].join(','),
            });

            contractList.addLoadedLegalEntitiesIds(notLoadedIds);
            contractList.add(contracts);
        }
    }

    public async createContract(contract: ContractCreationParamsData): Promise<void> {
        await ContractApi.create(contract);
        const created = await ContractApi.get(contract.id);
        store.contractList.add([created]);
    }

    public async updateContract(contractId: string, contract: ContractUpdateParamsData): Promise<void> {
        await ContractApi.update(contractId, contract);
        store.contractList.update(contractId, contract);
    }
}

export const contractService = new ContractService();
